import { ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { GlobalMessageType, RoutingService } from '@spartacus/core'
import { finalize } from 'rxjs/operators'
import { CustomRoutes } from '../../../../models/enum/custom-routes.enum'
import { SharedValue } from '../../../../models/enum/shared.enum'
import { ICsv } from '../../../../models/interface/csv.interface'
import { ISharedPagination } from '../../../../models/interface/shared.interface'
import { RebateClaim, RebateClaimsResults } from '../../../../models/rebate-claim.model'
import { RebateClaimService } from '../../../../services/rebate-claims/rebate-claim.service'
import { CsvService } from '../../../../services/shared/csv/csv.service'
import { CustomGlobalMessageService } from '../../../../services/shared/custom-global-message/custom-global-message.service'
import { SharedService } from '../../../../services/shared/shared.service'
import { ICustomSelectOption } from '../../../shared/custom-components/custom-select/modals/interface/custom-select.interface'
import { CustomTableColumnType } from '../../../shared/custom-components/custom-table/modals/enum/custom-table.enum'
import {
  ICustomTableColumn,
  ICustomTableHeader
} from '../../../shared/custom-components/custom-table/modals/interface/custom-table.interface'

@Component({
    selector: 'app-rebate-claims-history',
    templateUrl: './rebate-claims-history.component.html',
    styleUrls: ['./rebate-claims-history.component.scss'],
    standalone: false
})
export class RebateClaimsHistoryComponent implements OnInit {
  public rebateClaimsResults: RebateClaimsResults
  public showLoaderOnPagination: boolean
  public selectedSort: string
  public selectedPage: number = 1

  public options: ICustomSelectOption[] = [{ label: 'shared.sort.byDateCreated', value: 'byClaimDate' }]

  public headers: ICustomTableHeader[] = [
    { title: 'rebateClaims.rebateClaimHistory.claimId' },
    { title: 'rebateClaims.rebateClaimHistory.claimDate' },
    { title: 'rebateClaims.claimMonth' },
    { title: 'shared.value' },
    { title: 'shared.status' },
    { title: 'shared.actions', type: CustomTableColumnType.BUTTONS }
  ]

  public columns: ICustomTableColumn[] = [
    { key: 'rebateClaimCode', type: CustomTableColumnType.LINK, link: { handler: (res) => this.viewDetails(res) } },
    { key: 'claimDate', type: CustomTableColumnType.DATE },
    { key: 'calendarMonth' },
    { key: 'formattedClaimAmount' },
    { key: 'status' },
    {
      key: 'actions',
      type: CustomTableColumnType.BUTTONS,
      buttons: [
        {
          title: 'shared.details',
          translate: true,
          handler: (res) => this.viewDetails(res),
          icon: 'fa fa-info-circle'
        },
        {
          title: 'rebateClaims.rebateClaimHistory.downloadClaimCSV',
          translate: true,
          handler: (res) => this.downloadDetailsCSV(res),
          icon: 'fa fa-download'
        },
        {
          key: 'downloadCreditNote',
          title: 'shared.downloadCreditNote',
          translate: true,
          icon: 'fa fa-download',
          handler: (res) => this.downloadCreditNote(res.rebateClaimCode, res.creditNoteId)
        },
        {
          key: 'downloadDebitNote',
          title: 'shared.downloadDebitNote',
          translate: true,
          icon: 'fa fa-download',
          handler: (res) => this.downloadDebitNote(res.rebateClaimCode, res.debitNoteId)
        }
      ]
    }
  ]

  constructor(
    public routingService: RoutingService,
    private rebateClaimService: RebateClaimService,
    public globalMessageService: CustomGlobalMessageService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected csvService: CsvService,
    protected sharedService: SharedService
  ) {}

  ngOnInit(): void {
    this.sharedService
      .getQueryParams()
      .subscribe((queryParams) => {
        if (queryParams?.page) {
          this.pageChange(queryParams.page)
        } else {
          this.pageChange(1)
        }
      })
      .unsubscribe()
  }

  onNewClaimClicked(): void {
    this.routingService.go({
      cxRoute: CustomRoutes.CREATE_REBATE_CLAIM
    })
  }

  public viewDetails(value: any): void {
    const queryParam = {
      [CustomRoutes.REBATE_CLAIMS]: { page: this.selectedPage }
    }
    this.sharedService.setQueryParamsLocal(queryParam)

    // TODO:Spartacus - 'RoutingService.go' changed signature. Before 4.0, the object with query params could be passed in the 2nd argument. Now the 2nd argument is Angular NavigationExtras object (with 'queryParams' property).
    this.routingService.go({
      cxRoute: CustomRoutes.REBATE_CLAIM_DETAILS,
      params: { code: value?.rebateClaimCode }
    })
  }

  public downloadDetailsCSV(selectedRebateClaim: RebateClaim): void {
    if (selectedRebateClaim && selectedRebateClaim.rebateClaimCode) {
      this.rebateClaimService.getRebateClaimByCode(selectedRebateClaim.rebateClaimCode).subscribe(
        (rebateClaim: RebateClaim) => {
          this.generateCSV(rebateClaim, selectedRebateClaim.rebateClaimCode)
        },
        (err) => {
          this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
        }
      )
    }
  }

  public generateCSV(rebateClaim: RebateClaim, rebateClaimCode: string): void {
    const csv: ICsv = {
      title: `Rebate Claim details ${rebateClaimCode}`,
      columns: [
        { title: 'shared.date', key: 'date', type: CustomTableColumnType.DATE },
        { title: 'rebateClaims.distributorNumber', key: 'distributorNo' },
        { title: 'rebateClaims.endCustomerNumber', key: 'endCustomerNo' },
        { title: 'shared.sku', key: 'sku' },
        { title: 'shared.volume', key: 'originalQuantity' },
        { title: 'shared.price', key: 'originalPrice' },
        { title: 'shared.uom', key: 'originalUom' },
        { title: 'shared.total', key: 'total' }
      ],
      rows: rebateClaim.items
    }
    this.csvService.generateCSV(csv)
  }

  public pageChange(page: number): void {
    this.routingService.go(
      {
        cxRoute: CustomRoutes.REBATE_CLAIMS
        // Sagar
        // }, null, {queryParams: {page: page}});
      },
      { queryParams: { page: page } }
    )

    this.selectedPage = page
    this.changePageData({
      pageNumber: page,
      sort: this.selectedSort
    })
  }

  public sortChange(value: string): void {
    this.selectedSort = value
    this.changePageData({
      sort: value
    })
  }

  public changePageData(params: ISharedPagination): void {
    //  this.showLoaderOnPagination = true;
    //vimal
    this.rebateClaimService
      .getRebateHistory(params)
      .pipe(
        finalize(() => {
          this.showLoaderOnPagination = false
          this.changeDetectorRef.detectChanges()
        })
      )
      .subscribe((res) => {
        if (!res) {
          this.globalMessageService.add(SharedValue.errorTryAgain, GlobalMessageType.MSG_TYPE_ERROR)
        } else {
          this.rebateClaimsResults = res
        }
      })
  }

  public get showLoaderOnStartupOrWhenNoData(): boolean {
    return !this.rebateClaimsResults?.results?.length && this.showLoaderOnPagination
  }

  public downloadCreditNote(rebateClaimCode: string, documentId: string): void {
    if (!documentId) {
      return
    }

    this.rebateClaimService.downloadCreditNote(rebateClaimCode, documentId).subscribe(
      (res) => {
        this.rebateClaimService.generateCreditPDF(res)
      },
      (err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      }
    )
  }

  public downloadDebitNote(rebateClaimCode: string, documentId: string): void {
    if (!documentId) {
      return
    }

    this.rebateClaimService.downloadDebitNote(rebateClaimCode, documentId).subscribe(
      (res) => {
        this.rebateClaimService.generateDebitPDF(res)
      },
      (err) => {
        this.globalMessageService.add(err.message, GlobalMessageType.MSG_TYPE_ERROR)
      }
    )
  }
}
